import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import TranslationContext from '../../../../contextProviders/TranslationContext';
import Carousel from '../../../../commonComponents/carousel/Carousel';
import ServerSideCarousel from '../../../../commonComponents/carousel/ServerSideCarousel';
import { JOURNEY_STATUS } from '../../../../services/utilityFunctions';

const JOURNEY_TYPES = {
  REWARD: 'reward',
  TROPHY: 'trophy',
};

const getNumberRewards = (journeySteps, type) => {
  let number = 0;
  for (let i = 0; i < journeySteps.length; i += 1) {
    if (journeySteps[i].rewards) {
      const { rewards } = journeySteps[i];
      if (type === JOURNEY_TYPES.REWARD) {
        if (rewards.coins.wallet > 0) {
          number += 1;
        }
        if (rewards.coupons && rewards.coupons.length > 0) {
          number += 1;
        }
        if (rewards.vouchers && rewards.vouchers.length > 0) {
          number += 1;
        }
      } else if (type === JOURNEY_TYPES.TROPHY) {
        if (rewards.trophies) {
          for (const key in rewards.trophies) {
            if (rewards.trophies[key] > 0) {
              number += 1;
            }
          }
        }
      }
    }
  }
  return number;
};

export default function JourneyCarouselDashboard({ t }) {
  const containerClass = cx(
    'm-portlet',
    'm-portlet--rounded',
    'm-portlet--rounded-force',
  );
  const myJourneysUrl = '/table/journey/list';

  const handleRedirectToJourney = useCallback(slug => {
    window.location.href = `/journey/${slug}?urlBack=/dashboard`;
  }, []);
  const renderFirstJourney = firstJourney => {
    const image = firstJourney.image
      ? firstJourney.image.thumb
      : '/images/no_image_upload.png';
    const progressText = `${firstJourney.progress}%`;
    return (
      <button
        onClick={() => handleRedirectToJourney(firstJourney.slug)}
        type="button"
        className="journey-item-wrapper"
      >
        <div className="my-journey-thumbnail">
          <div className="journey-thumbnail-item">
            <img src={image} alt={firstJourney.title} />
          </div>
        </div>
        <div className="journey-info">
          <div className="journey-info-title">
            <span className="title">{firstJourney.title}</span>
          </div>
          <div className="journey-progress">
            <span>{t('journey.carousel.progress.title')}</span>
            <div className="progress-container">
              <span>{progressText}</span>
              <div className="progress">
                <div
                  style={{ width: progressText }}
                  className={`progress-bar ${
                    firstJourney.progress > 50 ? 'green' : 'orange'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      </button>
    );
  };

  const renderJourney = myJourney => {
    const numberRewards = getNumberRewards(
      myJourney.steps,
      JOURNEY_TYPES.REWARD,
    );
    const numberSteps = myJourney.steps.length;
    const numberTrophies = getNumberRewards(
      myJourney.steps,
      JOURNEY_TYPES.TROPHY,
    );
    const image = myJourney.image
      ? myJourney.image.thumb
      : '/images/no_image_upload.png';
    return (
      <button
        onClick={() => handleRedirectToJourney(myJourney.slug)}
        type="button"
        className="journey-item-wrapper"
      >
        <div className="journey-thumbnail-item">
          <img src={image} alt={myJourney.title} />
        </div>
        <div className="journey-info">
          <div className="journey-info-title">{myJourney.title}</div>
          <div className="journey-items">
            {numberSteps > 0 && (
              <span className="journey-steps-rewards-trophies">
                <img
                  src="/images/journey/steps.svg"
                  alt="Number of steps"
                  className="journey-icon"
                />
                {numberSteps === 1 && (
                  <span className="value">{`${numberSteps} ${t(
                    'journey.carousel.step',
                  )}`}</span>
                )}
                {numberSteps > 1 && (
                  <span className="value">{`${numberSteps} ${t(
                    'journey.carousel.steps',
                  )}`}</span>
                )}
              </span>
            )}
            {numberTrophies > 0 && (
              <span className="journey-steps-rewards-trophies">
                <img
                  src="/images/journey/trophy2.svg"
                  alt="Number of trophies"
                  className="journey-icon"
                />
                {numberTrophies === 1 && (
                  <span className="value">{`${numberTrophies} ${t(
                    'journey.carousel.trophy',
                  )}`}</span>
                )}
                {numberTrophies > 1 && (
                  <span className="value">{`${numberTrophies} ${t(
                    'journey.carousel.trophies',
                  )}`}</span>
                )}
              </span>
            )}
            {numberRewards > 0 && (
              <span className="journey-steps-rewards-trophies">
                <img
                  src="/images/journey/reward.svg"
                  alt="Number of trophies"
                  className="journey-icon"
                />
                {numberRewards === 1 && (
                  <span className="value">{`${numberRewards} ${t(
                    'journey.carousel.reward',
                  )}`}</span>
                )}
                {numberRewards > 1 && (
                  <span className="value">{`${numberRewards} ${t(
                    'journey.carousel.rewards',
                  )}`}</span>
                )}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <TranslationContext.Provider value={t}>
      <div className="row no-gutters">
        <div className="col-12">
          <div className={containerClass}>
            <div className="m-portlet__head remove-border-challenges row">
              <div className="m-portlet__head-caption col-12 col-lg-3">
                <div className="m-portlet__head-title">
                  <h3 className="m-portlet__head-text">{t('journeys')}</h3>
                </div>
              </div>
            </div>
            <Carousel>
              <ServerSideCarousel
                id="my-journeys-carousel"
                fetchUrl={myJourneysUrl}
                defaultFetchError="error_fetchMyJourneys"
                fetchHeaders={{
                  'Accept-Version': '1.0.0',
                  'X-Gamification-API': 'Games',
                  'Content-Type': 'application/json',
                }}
              >
                {items =>
                  items.map(journey => (
                    <Fragment key={journey._id}>
                      {journey.status === JOURNEY_STATUS.IN_PROGRESS
                        ? renderFirstJourney(journey)
                        : renderJourney(journey)}
                    </Fragment>
                  ))
                }
              </ServerSideCarousel>
            </Carousel>
          </div>
        </div>
      </div>
    </TranslationContext.Provider>
  );
}

JourneyCarouselDashboard.propTypes = {
  t: PropTypes.func.isRequired,
};

JourneyCarouselDashboard.defaultProps = {};
